import React, { useEffect } from 'react'
import { useLazyQuery } from '@apollo/client'
import LazyHydrate from 'react-lazy-hydration'
import { getContentfulClient } from '@/services/ApolloClient'
import { GET_FEATURED_PROJECT_BY_ID_QUERY } from '@/services/CmsService/FeaturedProject'
import { useUserRegion } from '@/services/UserRegionService'
import { FeaturedProject, GetFeaturedProjectByIdQuery } from '@/types/codegen-contentful'
import { useLocale } from '@/utils/LocaleUtil'
import { logger } from '@/utils/logging'
import { FeaturedProjectDisplay } from './FeaturedProjectDisplay'
import { FeaturedProjectPlaceholder } from './FeaturedProjectPlaceholder'

export interface FeaturedProjectSectionProps {
  featuredProjectId: string
  preview?: boolean
  testId?: string
}
export const FeaturedProjectSection: React.FC<FeaturedProjectSectionProps> = ({
  preview = false,
  featuredProjectId,
  testId,
}) => {
  const { locale } = useLocale()
  const { region } = useUserRegion()

  const client = getContentfulClient({ locale, preview })

  const [getFeaturedProjectById, { data, loading, error, called }] = useLazyQuery<GetFeaturedProjectByIdQuery>(
    GET_FEATURED_PROJECT_BY_ID_QUERY,
    {
      client,
      variables: { id: featuredProjectId, region, locale, preview },
      errorPolicy: 'all',
      onError: (error) => {
        logger().error(
          'FeaturedProjectSection query failed',
          {
            featuredProjectId,
            locale,
          },
          error,
        )
      },
      onCompleted: (res) => {
        const items = res?.featuredProjectCollection?.items
        if (!Array.isArray(items) || items.length === 0 || !items[0]) {
          logger().warn('FeaturedProjectSection returned no items', {
            res,
            featuredProjectId,
            locale,
          })
        }
      },
    },
  )

  useEffect(() => {
    try {
      getFeaturedProjectById({ variables: { id: featuredProjectId, locale, preview } })
    } catch (error) {
      logger().error(
        'FeaturedProjectSection query invocation error',
        {
          featuredProjectId,
          locale,
        },
        error,
      )
    }
  }, [featuredProjectId, getFeaturedProjectById, locale, preview])

  const featuredProject = data?.featuredProjectCollection?.items?.find(Boolean) ?? undefined

  if (called && !loading && !error && !featuredProject) {
    return null
  }

  return (
    <section id="featured" data-testid={testId ?? featuredProjectId} aria-busy={loading ? 'true' : 'false'}>
      <LazyHydrate whenVisible>
        <>
          {(loading || (!!error && !featuredProject)) && <FeaturedProjectPlaceholder />}

          {featuredProject && <FeaturedProjectDisplay featuredProject={featuredProject as FeaturedProject} />}
        </>
      </LazyHydrate>
    </section>
  )
}
